/**
 * Instructor Registration Page
 *
 * Allows dive instructors to apply for the referral program.
 * Upon approval, instructors receive free course promo codes for students.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import {
  registerInstructor,
  getInstructorProfile,
  CERTIFYING_AGENCIES,
  type RegisterInstructorData,
} from '@/services/instructorService';
import type { CertifyingAgency, InstructorStatus } from '@/types/models';

export function InstructorRegister() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingStatus, setExistingStatus] = useState<InstructorStatus | null>(null);

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [organization, setOrganization] = useState('');
  const [certifyingAgency, setCertifyingAgency] = useState<CertifyingAgency | ''>('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [referralReason, setReferralReason] = useState('');

  useEffect(() => {
    async function checkExistingStatus() {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const profile = await getInstructorProfile();
        if (profile.exists && profile.instructor) {
          setExistingStatus(profile.instructor.status);
          if (profile.instructor.status === 'approved') {
            // Redirect to dashboard if already approved
            navigate('/instructor/dashboard');
          }
        }
      } catch (err) {
        console.error('Error checking instructor status:', err);
      } finally {
        setLoading(false);
      }
    }

    checkExistingStatus();
  }, [currentUser, navigate]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!displayName.trim()) {
      setError('Please enter your full name');
      return;
    }

    try {
      setError('');
      setSubmitting(true);

      const data: RegisterInstructorData = {
        displayName: displayName.trim(),
        organization: organization.trim() || undefined,
        certifyingAgency: certifyingAgency || undefined,
        phone: phone.trim() || undefined,
        website: website.trim() || undefined,
        referralReason: referralReason.trim() || undefined,
      };

      await registerInstructor(data);
      setSuccess(true);
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!currentUser) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--cld-color-primary)' }}
      >
        <div
          className="max-w-md w-full p-8 text-center"
          style={{
            backgroundColor: 'var(--cld-color-white)',
            borderRadius: 'var(--cld-radius-md)',
            boxShadow: 'var(--cld-shadow-xl)',
          }}
        >
          <h1
            className="text-2xl font-bold mb-4"
            style={{ color: 'var(--cld-color-primary-dark)', fontFamily: 'var(--cld-font-display)' }}
          >
            Sign In Required
          </h1>
          <p style={{ color: 'var(--cld-color-charcoal)', marginBottom: '24px' }}>
            Please sign in or create an account to register as an instructor.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/signin"
              className="px-6 py-2"
              style={{
                backgroundColor: 'var(--cld-color-secondary)',
                color: 'var(--cld-color-white)',
                borderRadius: 'var(--cld-radius-sm)',
                fontWeight: 'var(--cld-font-weight-semibold)',
              }}
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="px-6 py-2"
              style={{
                backgroundColor: 'var(--cld-color-white)',
                color: 'var(--cld-color-secondary)',
                border: '1px solid var(--cld-color-secondary)',
                borderRadius: 'var(--cld-radius-sm)',
                fontWeight: 'var(--cld-font-weight-semibold)',
              }}
            >
              Sign Up
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <Layout showNav={false} activePage={null}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
              style={{ borderColor: 'var(--cld-color-secondary)', borderTopColor: 'transparent' }}
            />
            <p className="mt-4" style={{ color: 'var(--cld-color-charcoal)' }}>
              Loading...
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // Show pending status message
  if (existingStatus === 'pending') {
    return (
      <Layout showNav={false} activePage={null}>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div
            className="p-8 text-center"
            style={{
              backgroundColor: 'var(--cld-color-white)',
              borderRadius: 'var(--cld-radius-lg)',
              boxShadow: 'var(--cld-shadow-md)',
            }}
          >
            <div
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(0, 180, 216, 0.1)' }}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="var(--cld-color-secondary)"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1
              className="text-2xl font-bold mb-4"
              style={{ color: 'var(--cld-color-primary-dark)', fontFamily: 'var(--cld-font-display)' }}
            >
              Application Pending
            </h1>
            <p style={{ color: 'var(--cld-color-charcoal)', lineHeight: 1.6, marginBottom: '24px' }}>
              Your instructor application is currently being reviewed. We'll notify you by email once
              a decision has been made. This typically takes 1-2 business days.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2"
              style={{
                backgroundColor: 'var(--cld-color-secondary)',
                color: 'var(--cld-color-white)',
                borderRadius: 'var(--cld-radius-sm)',
                fontWeight: 'var(--cld-font-weight-semibold)',
              }}
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Show success message
  if (success) {
    return (
      <Layout showNav={false} activePage={null}>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div
            className="p-8 text-center"
            style={{
              backgroundColor: 'var(--cld-color-white)',
              borderRadius: 'var(--cld-radius-lg)',
              boxShadow: 'var(--cld-shadow-md)',
            }}
          >
            <div
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)' }}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="var(--cld-color-success)"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1
              className="text-2xl font-bold mb-4"
              style={{ color: 'var(--cld-color-primary-dark)', fontFamily: 'var(--cld-font-display)' }}
            >
              Application Submitted!
            </h1>
            <p style={{ color: 'var(--cld-color-charcoal)', lineHeight: 1.6, marginBottom: '24px' }}>
              Thank you for applying to the ClosedLoopDiving Instructor Referral Program. We'll
              review your application and notify you by email within 1-2 business days.
            </p>
            <p style={{ color: 'var(--cld-color-charcoal)', lineHeight: 1.6, marginBottom: '24px' }}>
              Once approved, you'll receive <strong>10 free course codes</strong> to share with your
              students.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2"
              style={{
                backgroundColor: 'var(--cld-color-secondary)',
                color: 'var(--cld-color-white)',
                borderRadius: 'var(--cld-radius-sm)',
                fontWeight: 'var(--cld-font-weight-semibold)',
              }}
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showNav={false} activePage={null}>
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div
          className="p-8"
          style={{
            backgroundColor: 'var(--cld-color-white)',
            borderRadius: 'var(--cld-radius-lg)',
            boxShadow: 'var(--cld-shadow-md)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-bold mb-2"
              style={{ color: 'var(--cld-color-primary-dark)', fontFamily: 'var(--cld-font-display)' }}
            >
              Instructor Referral Program
            </h1>
            <p style={{ color: 'var(--cld-color-charcoal)', lineHeight: 1.6 }}>
              Join our referral program and receive <strong>10 free course codes</strong> to share
              with your students. Help them access quality technical diving education while growing
              your training business.
            </p>
          </div>

          {/* Benefits */}
          <div
            className="mb-8 p-4"
            style={{
              backgroundColor: 'rgba(0, 180, 216, 0.05)',
              borderRadius: 'var(--cld-radius-md)',
              border: '1px solid rgba(0, 180, 216, 0.2)',
            }}
          >
            <h2
              className="text-lg font-semibold mb-3"
              style={{ color: 'var(--cld-color-primary-dark)' }}
            >
              Program Benefits
            </h2>
            <ul className="space-y-2" style={{ color: 'var(--cld-color-charcoal)' }}>
              <li className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  fill="var(--cld-color-success)"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>10 free course codes (100% off any course)</span>
              </li>
              <li className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  fill="var(--cld-color-success)"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Track code usage in your instructor dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  fill="var(--cld-color-success)"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Request additional codes as needed</span>
              </li>
              <li className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  fill="var(--cld-color-success)"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Help your students prepare with AI-powered learning</span>
              </li>
            </ul>
          </div>

          {/* Error message */}
          {error && (
            <div
              className="px-4 py-3 mb-6"
              style={{
                backgroundColor: 'rgba(214, 40, 40, 0.1)',
                border: '1px solid var(--cld-color-error)',
                color: 'var(--cld-color-error)',
                borderRadius: 'var(--cld-radius-sm)',
              }}
            >
              {error}
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <label
                htmlFor="displayName"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
              >
                Full Name <span style={{ color: 'var(--cld-color-error)' }}>*</span>
              </label>
              <input
                id="displayName"
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 focus:outline-none focus:ring-2"
                style={{
                  height: '44px',
                  border: '1px solid var(--cld-color-gray)',
                  borderRadius: 'var(--cld-radius-sm)',
                  color: 'var(--cld-color-charcoal)',
                  fontFamily: 'var(--cld-font-body)',
                }}
                disabled={submitting}
                placeholder="John Smith"
              />
            </div>

            {/* Email (read-only) */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
              >
                Email
              </label>
              <input
                type="email"
                value={currentUser.email || ''}
                disabled
                className="w-full px-3"
                style={{
                  height: '44px',
                  border: '1px solid var(--cld-color-gray)',
                  borderRadius: 'var(--cld-radius-sm)',
                  color: 'var(--cld-color-gray)',
                  backgroundColor: 'var(--cld-color-off-white)',
                  fontFamily: 'var(--cld-font-body)',
                }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--cld-color-gray)' }}>
                This is your account email and cannot be changed here.
              </p>
            </div>

            {/* Organization */}
            <div>
              <label
                htmlFor="organization"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
              >
                Dive Shop / Organization
              </label>
              <input
                id="organization"
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="w-full px-3 focus:outline-none focus:ring-2"
                style={{
                  height: '44px',
                  border: '1px solid var(--cld-color-gray)',
                  borderRadius: 'var(--cld-radius-sm)',
                  color: 'var(--cld-color-charcoal)',
                  fontFamily: 'var(--cld-font-body)',
                }}
                disabled={submitting}
                placeholder="Blue Water Diving"
              />
            </div>

            {/* Certifying Agency */}
            <div>
              <label
                htmlFor="certifyingAgency"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
              >
                Primary Certifying Agency
              </label>
              <select
                id="certifyingAgency"
                value={certifyingAgency}
                onChange={(e) => setCertifyingAgency(e.target.value as CertifyingAgency)}
                className="w-full px-3 focus:outline-none focus:ring-2"
                style={{
                  height: '44px',
                  border: '1px solid var(--cld-color-gray)',
                  borderRadius: 'var(--cld-radius-sm)',
                  color: certifyingAgency ? 'var(--cld-color-charcoal)' : 'var(--cld-color-gray)',
                  backgroundColor: 'var(--cld-color-white)',
                  fontFamily: 'var(--cld-font-body)',
                }}
                disabled={submitting}
              >
                <option value="">Select an agency...</option>
                {CERTIFYING_AGENCIES.map((agency) => (
                  <option key={agency.value} value={agency.value}>
                    {agency.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
              >
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 focus:outline-none focus:ring-2"
                style={{
                  height: '44px',
                  border: '1px solid var(--cld-color-gray)',
                  borderRadius: 'var(--cld-radius-sm)',
                  color: 'var(--cld-color-charcoal)',
                  fontFamily: 'var(--cld-font-body)',
                }}
                disabled={submitting}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            {/* Website */}
            <div>
              <label
                htmlFor="website"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
              >
                Website
              </label>
              <input
                id="website"
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="w-full px-3 focus:outline-none focus:ring-2"
                style={{
                  height: '44px',
                  border: '1px solid var(--cld-color-gray)',
                  borderRadius: 'var(--cld-radius-sm)',
                  color: 'var(--cld-color-charcoal)',
                  fontFamily: 'var(--cld-font-body)',
                }}
                disabled={submitting}
                placeholder="https://www.example.com"
              />
            </div>

            {/* Referral Reason */}
            <div>
              <label
                htmlFor="referralReason"
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
              >
                Why do you want to refer students to ClosedLoopDiving?
              </label>
              <textarea
                id="referralReason"
                value={referralReason}
                onChange={(e) => setReferralReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 focus:outline-none focus:ring-2"
                style={{
                  border: '1px solid var(--cld-color-gray)',
                  borderRadius: 'var(--cld-radius-sm)',
                  color: 'var(--cld-color-charcoal)',
                  fontFamily: 'var(--cld-font-body)',
                  resize: 'vertical',
                }}
                disabled={submitting}
                placeholder="Tell us about your teaching focus and how ClosedLoopDiving can help your students..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                height: '48px',
                backgroundColor: 'var(--cld-color-accent)',
                color: 'var(--cld-color-white)',
                borderRadius: 'var(--cld-radius-sm)',
                fontWeight: 'var(--cld-font-weight-semibold)',
                fontFamily: 'var(--cld-font-body)',
                fontSize: 'var(--cld-font-size-base)',
                transition: 'var(--cld-transition-fast)',
                border: 'none',
              }}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {submitting ? 'Submitting Application...' : 'Submit Application'}
            </button>
          </form>

          {/* Back link */}
          <div className="mt-6 text-center">
            <Link
              to="/dashboard"
              className="text-sm hover:underline"
              style={{ color: 'var(--cld-color-secondary)' }}
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
